import * as React from 'react';

import { useLockFn, useMemoizedFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useBrandAction, useBrandValue } from 'context';
import { createBrand, updateBrand } from 'services';
import { replaceExtraSeparator } from 'utils';
import { PATHNAME } from 'utils';

import { BrandFormAction } from './action';
import { BrandFormTabs } from './tabs';

const {
  LOCAL: {
    BRANDS: { BASE_URL: BRANDS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Partial<Record<string, string>>;

const BrandForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    title = '',
    slug,
    bannerId,
    iconId,
    description,
    pictures = [],
    videos = [],
    seo,
    defaultTitle,
    mode,
    step,
  } = useBrandValue();
  const action = useBrandAction();
  const navigate = useNavigate();

  const formattedSlug = replaceExtraSeparator(slug ?? title);

  const handleUpdateBrandBaseInfo = useMemoizedFn(() => {
    action({
      type: 'UPDATE_BRAND_STEP',
      payload: {
        step: 2,
      },
    });
  });

  const handleUpdateBrandFilesInfo = useMemoizedFn((files: FieldType) => {
    action({
      type: 'UPDATE_BRAND_PICTURES',
      payload: {
        pictures: pictures
          .map(picture => {
            const { id } = picture;
            const title = files[`file-title-${id}`];
            const link = files[`file-link-${id}`];
            const watermark = Boolean(files[`file-watermark-${id}`]);

            return {
              ...picture,
              title,
              link,
              watermark,
            };
          })
          .sort((a, b) => {
            const aIndex = Number(files[`file-index-${a.id}`]);
            const bIndex = Number(files[`file-index-${b.id}`]);

            if (aIndex === bIndex) {
              return pictures.indexOf(a) - pictures.indexOf(b);
            }
            return aIndex - bIndex;
          }),
      },
    });
    action({
      type: 'UPDATE_BRAND_VIDEOS',
      payload: {
        videos: videos
          .map(video => {
            const { id } = video;
            const title = files[`file-title-${id}`];
            const link = files[`file-link-${id}`];
            const watermark = Boolean(files[`file-watermark-${id}`]);

            return {
              ...video,
              title,
              link,
              watermark,
            };
          })
          .sort((a, b) => {
            const aIndex = Number(files[`file-index-${a.id}`]);
            const bIndex = Number(files[`file-index-${b.id}`]);

            if (aIndex === bIndex) {
              return videos.indexOf(a) - videos.indexOf(b);
            }
            return aIndex - bIndex;
          }),
      },
    });
    action({
      type: 'UPDATE_BRAND_STEP',
      payload: {
        step: 3,
      },
    });
  });

  const handleUpdateBrandSeoInfo = useLockFn(async (files: FieldType) => {
    const isUpdatedMode = mode === 'Update';

    const params: Parameters<typeof updateBrand>[0] = {
      id,
    };
    const body: Parameters<typeof updateBrand>[1] = {
      title,
      slug: formattedSlug,
      bannerId,
      iconId,
      description,
      pictures: pictures.map(({ id, title, link, watermark }) => ({
        id,
        title,
        link,
        watermark,
      })),
      videos: videos.map(({ id, title, link, watermark }) => ({
        id,
        title,
        link,
        watermark,
      })),
      seo,
    };

    (isUpdatedMode ? updateBrand(params, body) : createBrand(body))
      .then(() => {
        notification.success({
          message: `برند ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
        });
        navigate(BRANDS_BASE_URL);
      })
      .catch(message => {
        notification.error({ message });
      });
  });

  const onFinish = useLockFn(async (fields: FieldType) => {
    switch (step) {
      case 1: {
        return handleUpdateBrandBaseInfo();
      }
      case 2: {
        return handleUpdateBrandFilesInfo(fields);
      }
      case 3: {
        return handleUpdateBrandSeoInfo(fields);
      }
      default:
        break;
    }
  });

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'title',
          value: title,
        },
        {
          name: 'slug',
          value: formattedSlug,
        },
        ...pictures.map(({ id }, index) => ({
          name: `file-index-${id}`,
          value: index + 1,
        })),
        ...videos.map(({ id }, index) => ({
          name: `file-index-${id}`,
          value: index + 1,
        })),
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <BrandFormTabs />
        <BrandFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { BrandForm };
